"use client";
import { InfoTooltip } from "@/ui/index";
import { ApprovableCell } from "@/features/permissions/components/ApprovableCell";
import { PermissionCell } from "@/features/permissions/components/PermissionCell";
import { PermissionsDatesCell } from "@/features/permissions/components/PermissionsDatesCell";
import { TooltipProvider } from "@gettactic/components/src/primitives/tooltip";
import {
  ConfirmOperationModal,
  IResourcePermissionWithResName
} from "@/features/reserve/components/resources/tabs/access-restrictions/RestrictionsTab";
import { cn } from "@gettactic/helpers/src/css/cn";
import { IResourcePermission } from "@gettactic/api";
import React, { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { DataMan } from "@gettactic/components/src/data-display/data-man/DataMan";
import { DataManColumDef } from "@gettactic/components/src/data-display/data-man/types/columns";
import { DataManAction } from "@gettactic/components/src/data-display/data-man/types/actions";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";

type Props = {
  className?: string;
  data: IResourcePermissionWithResName[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
};

function bulkKey(perm: IResourcePermission) {
  return `${perm.id}_${perm.resource_id}_${perm.user ? "user" : "team"}`;
}

export const PermissionTable = ({
  className,
  data: rawData,
  setOperationalModal
}: Props) => {
  const [query, setQuery] = useState("");

  const data = useMemo(() => {
    const q = query.toLowerCase().trim();
    const filtered = q.length
      ? rawData.filter(
          (x) =>
            x.resName.toLowerCase().includes(q) ||
            (x.user
              ? x.user.name.toLowerCase().includes(q) ||
                x.user.email.toLowerCase().includes(q)
              : x.team?.name.toLowerCase().includes(q))
        )
      : rawData;
    // Add computed _bulkId to each item for DataMan selection
    return filtered.map((item) => ({
      ...item,
      _bulkId: bulkKey(item)
    }));
  }, [query, rawData]);

  const columns: DataManColumDef<IResourcePermissionWithResName>[] = useMemo(
    () => [
      {
        key: "assignment",
        title: "Assignment",
        render: (permission) => <PermissionCell permission={permission} />
      },
      {
        key: "dates",
        title: "Dates",
        width: 160,
        render: (permission) => <PermissionsDatesCell permission={permission} />
      },
      {
        key: "resource",
        title: "Res.",
        width: 120,
        render: (permission) => (
          <div className="flex items-center justify-center space-x-1 text-center text-sm">
            {permission.resName}
          </div>
        )
      },
      {
        key: "accessGroupType",
        title: "Group",
        width: 100,
        render: (permission) => (
          <div className="flex items-center justify-center space-x-1 text-center text-sm">
            {permission.access_group_type}
          </div>
        )
      },
      {
        key: "approvalRequired",
        title: (
          <div className="flex items-center">
            <InfoTooltip>
              Approval Required
              <span className="font-normal">
                {" "}
                Users will need a manager to approve a reservation before it's
                accepted by the system.
              </span>
            </InfoTooltip>
          </div>
        ),
        width: 140,
        render: (permission) => (
          <ApprovableCell
            permission={permission}
            onChange={(x: IResourcePermission) => {
              setOperationalModal(
                x ? { operation: "is_approvable", applyTo: [x] } : null
              );
            }}
          />
        )
      }
    ],
    [setOperationalModal]
  );

  const actions: DataManAction[] = useMemo(
    () => [
      {
        id: "delete",
        label: "Delete",
        icon: Trash2 as React.FC,
        allowBulk: true,
        allowInline: true,
        onAction: ({ target, targetRecords }) => {
          const records =
            targetRecords.length > 0
              ? targetRecords
              : [target as IResourcePermissionWithResName];
          setOperationalModal({
            operation: "delete",
            applyTo: records
          });
        }
      }
    ],
    [setOperationalModal]
  );

  return (
    <TooltipProvider>
      <div
        className={cn(
          "flex flex-1 flex-col flex-nowrap items-stretch gap-3 overflow-hidden px-1",
          className
        )}
      >
        <div className="flex items-center justify-between gap-4 sm:gap-16">
          <SearchInput query={query} setQuery={setQuery} />
        </div>
        <div className="min-h-0 flex-1 relative">
          {data && data.length > 0 && (
            <DataMan
              data={data}
              columns={columns}
              actions={actions}
              allowBulkSelection
              dataIdKey="_bulkId"
              fullSize
              actionsColumnWidth={50}
            />
          )}
          {data.length === 0 && (
            <p className="mx-auto my-10 text-center text-xl">
              No permissions found.
            </p>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
